import React, { useContext, useEffect, useRef, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { getActiveRecurrence } from "~/functions/recurrenceFn";
import PageContainer from "~/components/PageContainer";
import { FullGridContext } from "../..";
import { format, parseISO } from "date-fns";
import Settlement from "./Settlement";
import { AlertContext, PageContext } from "~/App";
import { toast } from "react-toastify";
import api, { baseURL } from "~/services/api";
import { saveAs } from "file-saver";
import FeeAdjustment from "./FeeAdjustment";
import {
  invoiceTypeDetailsOptions,
  invoiceTypesOptions,
  receivableStatusesOptions,
} from "~/functions/selectPopoverOptions";
import Renegociation from "./Renegociation";
import Compensation from "./Compensation";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutlet,
  useSearchParams,
} from "react-router-dom";
import ModalFilters, { getStoredFilters } from "./ModalFilters";
import { Pin } from "lucide-react";

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencySign: "accounting",
});

function buildFiltersParamsFromStored(stored) {
  if (!stored) return null;
  const statusesCsv = (stored.statuses || []).filter(Boolean).join(",");
  const params = {
    statuses: statusesCsv || "null",
    status: statusesCsv || "null",
    student_status: stored.studentStatus || "null",
    student_id: stored.studentId || "null",
    student_name: stored.studentName?.trim() || "null",
    paymentmethod_id: stored.paymentMethodId || "null",
    show_summary: stored.showSummary ? "true" : "null",
    type: stored.invoiceType || "null",
    type_detail: stored.invoiceTypeDetail || "null",
    is_autopay: stored.autopay || "null",
    entry_date_from: stored.entryDateFrom || "null",
    entry_date_to: stored.entryDateTo || "null",
    due_date_from: stored.dueDateFrom || "null",
    due_date_to: stored.dueDateTo || "null",
    settlement_from: stored.settlementFrom || "null",
    settlement_to: stored.settlementTo || "null",
    cash_date_from: stored.cashDateFrom || "null",
    cash_date_to: stored.cashDateTo || "null",
    payment_method_from_settlement: stored.paymentMethodFromSettlement ? "true" : "null",
  };
  const hasValues = Object.values(params).some((val) => val && val !== "null");
  return hasValues ? params : null;
}

export function FinancialInbounds() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { pages } = useContext(PageContext);
  const paths = pathname.split("/");
  const redirectRouteName = pages
    .find((page) => page.name === paths[1])
    .children.find((page) => page.path === `/${paths[1]}/${paths[2]}`)
    .children[0].path;
  useEffect(() => {
    if (pathname.toUpperCase() === `/${paths[1]}/${paths[2]}`.toUpperCase()) {
      navigate(`${redirectRouteName}`);
    }
  }, [pathname]);
  return <Outlet />;
}

export default function FinancialReceivables() {
  const Outlet = useOutlet();
  const { pathname } = useLocation();
  const filial = useSelector((state) => state.auth.filial);
  const userId = useSelector((state) => state.auth.profile?.id);
  const [ownReceivablesOnly, setOwnReceivablesOnly] = useState(false);
  const requestVersion = useRef(0);
  const defaultOrderBy = { column: "invoice_number", asc: false };
  const defaultGridHeader = [
    {
      title: "Issuer Name",
      name: ["issuer", "name"],
      type: "text",
      filter: false,
    },
    {
      title: "Invoice Number",
      name: "invoice_number",
      type: "text",
      filter: false,
    },
    {
      title: "Due Date",
      name: "due_date",
      type: "date",
      filter: false,
    },
    {
      title: "Amount",
      name: "amount",
      type: "currency",
      filter: false,
    },
    {
      title: "Discount",
      name: "discount",
      type: "currency",
      filter: false,
    },
    {
      title: "Fee",
      name: "fee",
      type: "currency",
      filter: false,
    },
    {
      title: "Total",
      name: "total",
      type: "currency",
      filter: false,
    },
    {
      title: "Balance",
      name: "balance",
      type: "currency",
      filter: false,
    },
    {
      title: "Status",
      name: "status",
      type: "text",
      filter: true,
    },
    {
      title: "Autopay",
      name: "is_autopay",
      type: "boolean",
      filter: true,
    },
  ];
  const [selected, setSelected] = useState([]);
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [feeAdjustmentOpen, setFeeAdjustmentOpen] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [renegociationOpen, setRenegociationOpen] = useState(false);
  const [compensationOpen, setCompensationOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [summary, setSummary] = useState(null);
  const [filtersParams, setFiltersParams] = useState(() =>
    buildFiltersParamsFromStored(getStoredFilters())
  );
  const [filtersPinned, setFiltersPinned] = useState(
    () => localStorage.getItem("receivables_filters_pinned") === "true"
  );
  const defaultExcelData = [
    {
      title: "Entry date from",
      name: "entry_date_from",
      type: "date",
      value: null,
    },
    {
      title: "Entry date to",
      name: "entry_date_to",
      type: "date",
      value: null,
    },
    {
      title: "Due date from",
      name: "due_date_from",
      type: "date",
      value: null,
    },
    { title: "Due date to", name: "due_date_to", type: "date", value: null },
    {
      title: "Settlement date from",
      name: "settlement_from",
      type: "date",
      value: null,
    },
    {
      title: "Settlement date to",
      name: "settlement_to",
      type: "date",
      value: null,
    },
    {
      title: "Status",
      name: "status",
      type: "select",
      options: receivableStatusesOptions,
      value: "All",
    },
    {
      title: "Type",
      name: "type",
      type: "select",
      options: invoiceTypesOptions,
      value: "All",
    },
    {
      title: "Type Detail",
      name: "type_detail",
      type: "select",
      options: invoiceTypeDetailsOptions,
      value: "All",
    },
  ];
  const [excelData, setExcelData] = useState(defaultExcelData);
  const { alertBox } = useContext(AlertContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const findAccessByPath = (node, targetPath) => {
    if (!node) return null;
    if (node.path?.toUpperCase() === targetPath?.toUpperCase()) return node;

    if (!node.children || node.children.length === 0) return null;
    for (const child of node.children) {
      const found = findAccessByPath(child, targetPath);
      if (found) return found;
    }

    return null;
  };

  const pageAccessByPath = findAccessByPath(accessModule, pathname);
  const pageAccessByAlias = accessModule.children.find(
    (el) => el.alias === "financial-receivables"
  );
  const pageAccess = pageAccessByPath || pageAccessByAlias;

  useEffect(() => {
    console.log("[Financial/Receivables/Receivables] pageAccess resolved", {
      pathname,
      resolvedAlias: pageAccess?.alias,
      resolvedPath: pageAccess?.path,
      childrenAliases: pageAccess?.children?.map((child) => child.alias) || [],
    });
  }, [pathname, pageAccess]);

  useEffect(() => {
    setActiveFilters([]);
  }, []);

  const mapReceivables = (data = []) =>
    data
      .sort((a, b) => (a.due_date > b.due_date ? 1 : -1))
      .map(
        (
          {
            id,
            canceled_at,
            filial,
            issuer,
            invoice_number,
            due_date,
            amount,
            discount,
            fee,
            total,
            balance,
            status,
          },
          index
        ) => {
          return {
            show: true,
            id,
            fields: [
              issuer.name,
              invoice_number.toString(),
              format(parseISO(due_date), "yyyy-MM-dd"),
              "$ " + amount.toFixed(2),
              "$ " + discount.toFixed(2),
              "$ " + fee.toFixed(2),
              "$ " + total.toFixed(2),
              "$ " + balance.toFixed(2),
              status?.replace(/_/g, " "),
              getActiveRecurrence(issuer)?.is_autopay ? true : false,
            ],
            selectable:
              status.includes("Pending") || status.includes("Partial Paid"),
            attention: {
              title: "Due Date",
              show:
                status.includes("Pending") &&
                due_date < format(new Date(), "yyyy-MM-dd"),
            },
            canceled: canceled_at,
            page: Math.ceil((index + 1) / limit),
          };
        }
      );

  async function loader() {
    const version = ++requestVersion.current;
    setLoadingData(true);
    setGridData([]);
    setSummary(null);
    const params = new URLSearchParams();
    params.set("limit", limit);
    params.set("page", page);
    params.set("orderBy", orderBy?.column || defaultOrderBy.column);
    params.set("orderASC", orderBy ? (orderBy.asc ? "ASC" : "DESC") : defaultOrderBy.asc ? "ASC" : "DESC");
    params.set("search", search?.value || "");
    Object.entries(filtersParams || {}).forEach(([key, value]) => {
      params.set(key, value || "null");
    });
    try {
      const { data } = await api.get(`/receivables?${params.toString()}`);
      if (version !== requestVersion.current) return;
      const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
      setPages(pagesCalc);
      setTotalRows(data?.totalRows || 0);
      setGridDetails({ totalRows: data?.totalRows || 0, pages: pagesCalc });
      setGridData(mapReceivables(data?.rows || []));
      setSummary(data?.summary || null);
      setOwnReceivablesOnly(data?.access?.own_only === true);
    } catch (err) {
      if (version !== requestVersion.current) return;
      setGridDetails({ totalRows: 0, pages: 1 });
      setTotalRows(0);
      setPages(1);
      toast.error(err.response?.data?.error || "Could not load receivables.");
    } finally {
      if (version === requestVersion.current) setLoadingData(false);
    }
  }

  const applyFiltersToGrid = async (queryString) => {
    const version = ++requestVersion.current;
    setApplyingFilters(true);
    setLoadingData(true);
    setGridData([]);
    setSummary(null);
    try {
      const { data } = await api.get(queryString);
      if (version !== requestVersion.current) return;
      const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
      setPages(pagesCalc);
      setGridDetails({
        totalRows: data?.totalRows || 0,
        pages: pagesCalc,
      });
      setGridData(mapReceivables(data?.rows || []));
      setSummary(data?.summary || null);
      setOwnReceivablesOnly(data?.access?.own_only === true);
      setTotalRows(data?.totalRows || 0);
      const parsed = new URLSearchParams(queryString.split("?")[1] || "");
      const newFilters = {};
      parsed.forEach((value, key) => {
        if (!["limit", "page", "orderBy", "orderASC", "search"].includes(key)) {
          newFilters[key] = value;
        }
      });
      const hasValues = Object.values(newFilters).some((val) => val && val !== "null");
      setFiltersParams(hasValues ? newFilters : null);
      setPage(1);
    } catch (err) {
      if (version !== requestVersion.current) return;
      setGridDetails({ totalRows: 0, pages: 1 });
      setTotalRows(0);
      setPages(1);
      toast.error(err.response?.data?.error || "Could not load receivables.");
    } finally {
      setApplyingFilters(false);
      if (version === requestVersion.current) setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!settlementOpen) {
      setSelected([]);
    }
    loader();
    if (searchParams?.has("id")) {
      const targetId = searchParams.get("id");
      setSearchParams("");
      setOpened(targetId);
    }
  }, [opened, settlementOpen, filial, userId, orderBy, search, limit, page, filtersParams]);

  function handleSettlement() {
    const newVarOpened = !settlementOpen;
    setSettlementOpen(newVarOpened);
    if (!newVarOpened) {
      loader();
    }
  }

  function handleRenegociation() {
    const newVarOpened = !renegociationOpen;
    setRenegociationOpen(newVarOpened);
    if (!newVarOpened) {
      setSelected([]);
      loader();
    }
  }

  function handleCompensation() {
    if (!compensationOpen && selected.length < 2) {
      alertBox({
        title: "Attention!",
        descriptionHTML:
          "<p>You must select at least 2 receivables to create a consolidation.</p>",
        buttons: [
          {
            title: "Ok",
            class: "cancel",
          },
        ],
      });
      return;
    }
    const newVarOpened = !compensationOpen;
    setCompensationOpen(newVarOpened);
    if (!newVarOpened) {
      setSelected([]);
      loader();
    }
  }

  useEffect(() => {
    let issuer = null;
    let differentIssuer = null;
    if (selected.length > 0) {
      selected.map(async (receivable) => {
        if (issuer) {
          if (receivable.fields[0] !== issuer) {
            differentIssuer = receivable.fields[0];
          }
        }
        issuer = receivable.fields[0];
      });
    }
    if (differentIssuer) {
      alertBox({
        title: "Attention!",
        descriptionHTML: `<p>You have selected receivables from different issuers. Please select only receivables from the same issuer.</p>`,
        buttons: [
          {
            title: "Ok",
            class: "cancel",
          },
        ],
      });
      setSelected(
        selected.filter(
          (receivable) => receivable.fields[0] !== differentIssuer
        )
      );
    }
  }, [selected]);

  async function handleDelete() {
    if (selected.length === 0) {
      return;
    }
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to delete the selected receivables? This action cannot be undone.",
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            const promises = [];
            let hasChanged = false;
            selected.map((receivable) => {
              promises.push(
                api
                  .delete(`/receivables/${receivable.id}`)
                  .then((response) => {
                    toast(response.data.message, { autoClose: 1000 });
                  })
                  .catch((err) => {
                    toast(err.response.data.error, {
                      type: "error",
                      autoClose: 3000,
                    });
                  })
              );
            });
            Promise.all(promises).then(() => {
              setSelected([]);
              loader();
            });
          },
        },
      ],
    });
  }

  async function handleExcel(generate = true) {
    if (excelOpen && generate) {
      api
        .post(`/receivables/excel`, {
          entry_date_from: excelData[0].value,
          entry_date_to: excelData[1].value,
          due_date_from: excelData[2].value,
          due_date_to: excelData[3].value,
          settlement_from: excelData[4].value,
          settlement_to: excelData[5].value,
          status: excelData[6].value,
          type: excelData[7].value,
          type_detail: excelData[8].value,
        })
        .then(({ data }) => {
          saveAs(`${baseURL}/get-file/${data.name}`, `${data.name}.xlsx`);
          // setExcelData(defaultExcelData);
        })
        .catch((err) => {
          console.log(err);
        });
    }
    setExcelOpen(!excelOpen);
  }

  async function handleFeeAdjustment() {
    if (selected.length === 0) {
      return;
    }
    if (selected.length > 1) {
      alertBox({
        title: "Attention!",
        descriptionHTML:
          "<p>You can only adjust fees of one receivable at a time.</p>",
        buttons: [
          {
            title: "Ok",
            class: "cancel",
            onPress: () => {
              setSelected([]);
            },
          },
        ],
      });
      return;
    }
    const newVarOpened = !feeAdjustmentOpen;
    setFeeAdjustmentOpen(newVarOpened);
    if (!newVarOpened) {
      loader();
    }
  }

  return (
    Outlet || (
      <>
        <ModalFilters
          opened={filtersModalOpen}
          applying={applyingFilters}
          onClose={() => {
            setFiltersModalOpen(false);
            setFiltersPinned(
              localStorage.getItem("receivables_filters_pinned") === "true"
            );
          }}
          onApplyFilters={applyFiltersToGrid}
        />
        <PageContainer
          FullGridContext={FullGridContext}
          PagePreview={PagePreview}
          defaultGridHeader={defaultGridHeader}
          summaryRow={summary ? {
            fields: [
              `Summary (${summary.count})`, "", "",
              ...["amount", "discount", "fee", "total", "balance"].map(
                (field) => USDollar.format(Number(summary[field] || 0))
              ),
              "", "",
            ],
          } : null}
          headerContent={ownReceivablesOnly ? (
            <div className="mb-3 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900" role="status">
              You can only view and manage receivables created by you. Totals and exports follow this restriction.
            </div>
          ) : null}
          pageAccess={pageAccess}
          selection={{
            multiple: true,
            selected,
            setSelected,
            functions: [
              {
                title: "Fee Adjustment",
                fun: handleFeeAdjustment,
                icon: "Pencil",
                alias: "fee-adjustments",
                Page: FeeAdjustment,
                opened: feeAdjustmentOpen,
                setOpened: setFeeAdjustmentOpen,
                selected,
              },
              {
                title: "Settlement",
                alias: "settlement",
                fun: handleSettlement,
                icon: "ReplaceAll",
                Page: Settlement,
                opened: settlementOpen,
                setOpened: setSettlementOpen,
                selected,
              },
              {
                title: "Renegotiation",
                alias: "renegotiation",
                fun: handleRenegociation,
                icon: "Handshake",
                Page: Renegociation,
                opened: renegociationOpen,
                setOpened: setRenegociationOpen,
                selected,
              },
              {
                title: "Consolidation",
                alias: "receivables_consolidation",
                fun: handleCompensation,
                icon: "Combine",
                Page: Compensation,
                opened: compensationOpen,
                setOpened: setCompensationOpen,
                selected,
              },
              {
                title: "Delete",
                alias: "delete",
                fun: handleDelete,
                icon: "X",
                Page: null,
                opened: false,
                setOpened: () => null,
                selected,
              },
            ],
          }}
          filtersModal={{
            onOpen: () => setFiltersModalOpen(true),
            viewSelect: filtersPinned ? (
              <div className="flex items-center gap-1 text-orange-500 text-xs font-medium">
                <Pin size={13} className="fill-orange-500" />
                Filters Applied
              </div>
            ) : null,
          }}
        />
      </>
    )
  );
}
